"use client";

import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { cn } from "~/utils/fontsConfig";
import { BASE_INPUT_STYLES } from "./BASE_STYLE_DEFINITIONS";
import { RiCloseLine, RiEyeLine } from "react-icons/ri";
import { useIsVisible } from "~/hooks/useIsVisible";

export interface InputProps
    extends React.InputHTMLAttributes<HTMLInputElement> {
    prefixComponent?: React.ReactNode;
    suffixComponent?: React.ReactNode;
    isSuffixInteractive?: boolean;
    inputClassName?: string;
    onClear?: () => void;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
    (
        {
            className,
            inputClassName,
            prefixComponent,
            suffixComponent,
            isSuffixInteractive,
            type,
            ...props
        },
        ref,
    ) => {
        const [prefixWidth, setPrefixWidth] = useState(0);
        const [suffixWidth, setSuffixWidth] = useState(0);
        const refPrefix = useRef<HTMLDivElement>(null);
        const refSuffix = useRef<HTMLDivElement>(null);
        const localRef = useRef<HTMLDivElement>(null!);
        const isVisible = localRef && useIsVisible(localRef);

        useEffect(() => {
            if (refPrefix.current) {
                const width = refPrefix.current.getBoundingClientRect().width;
                setPrefixWidth(width);
            }
            if (refSuffix.current) {
                const width = refSuffix.current.getBoundingClientRect().width;
                setSuffixWidth(width);
            }
        }, [refPrefix, refSuffix, isVisible]);

        const inputStyles = cn(BASE_INPUT_STYLES, className, inputClassName);

        return (
            <div
                className={cn(
                    "relative flex h-max w-auto min-w-[0] flex-row items-center",
                    className,
                )}
                ref={localRef}
            >
                {prefixComponent && (
                    <div
                        className="pointer-events-none absolute inset-y-1 left-0 flex items-center"
                        ref={refPrefix}
                    >
                        <div className="pl-4">{prefixComponent}</div>
                    </div>
                )}
                <div className="flex min-h-[40px] w-full flex-col items-center">
                    <input
                        autoComplete="off"
                        type={type}
                        className={inputStyles}
                        style={{
                            paddingLeft: prefixComponent
                                ? prefixWidth + 4
                                : undefined,
                            paddingRight: suffixComponent
                                ? suffixWidth
                                : undefined,
                        }}
                        ref={ref}
                        {...props}
                    />
                </div>
                {suffixComponent && (
                    <div
                        className={cn(
                            `
                            absolute
                            inset-y-1
                            right-0
                            flex
                            items-center
                            pr-2`,
                            !isSuffixInteractive && "pointer-events-none",
                        )}
                        ref={refSuffix}
                    >
                        {suffixComponent}
                    </div>
                )}
                {props.onClear && (
                    <div
                        className={cn(
                            `
                                absolute
                                inset-y-1
                                right-0
                                flex
                                items-center
                                pr-2`,
                        )}
                        onClick={props.onClear}
                    >
                        <RiCloseLine className="cursor-pointer text-gray-500" />
                    </div>
                )}
            </div>
        );
    },
);

Input.displayName = "Input";

const MaskedInput = React.forwardRef<
    HTMLInputElement,
    React.InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => {
    const [isMasked, setIsMasked] = useState(true);
    return (
        <Input
            ref={ref}
            className={className}
            suffixComponent={
                <RiEyeLine
                    onClick={() => setIsMasked(!isMasked)}
                    className="z-100 mr-2 cursor-pointer text-gray-500 hover:text-v3-primary"
                />
            }
            isSuffixInteractive={true}
            type={isMasked ? "password" : "text"}
            {...props}
        />
    );
});

export { Input, MaskedInput };
